import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Bell, X, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import NotificationCard from "../../components/NotificationCard/NotificationCard.component";
import {
  getAllNotifications,
  deleteAllNotifications,
  toggleNotificationBar,
} from "../../redux/notifications/notificationActions";
import { Button } from "@/components/ui/button";

export default function Notification() {
  const dispatch = useDispatch();
  const notifications = useSelector(
    (state) => state.notifications.notifications
  );
  const notificationsOpen = useSelector(
    (state) => state.notifications.notificationsOpen
  );
  const user = useSelector((state) => state.user);
  const { t } = useTranslation();
  const drawerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        if (notificationsOpen) {
          // dispatch(toggleNotificationBar());
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [notificationsOpen, dispatch]);

  useEffect(() => {
    if (user?.currentUser?.user?._id && user?.currentUser?.token) {
      dispatch(
        getAllNotifications(user.currentUser.user._id, user.currentUser.token)
      );
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (notificationsOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [notificationsOpen]);

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        className={cn(
          "fixed top-4 right-4 z-[60] rounded-full shadow-md",
          "bg-card text-card-foreground hover:bg-muted"
        )}
        onClick={() => dispatch(toggleNotificationBar())}
        aria-label={notificationsOpen ? t("dashboard.notifications.close") : t("dashboard.notifications.open")}
      >
        {notificationsOpen ? (
          <X className="h-5 w-5" />
        ) : (
          <div className="relative">
            <Bell className="h-5 w-5" />
            {notifications.length > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] text-[10px] p-0.5 flex items-center justify-center rounded-full"
              >
                {notifications.length}
              </Badge>
            )}
          </div>
        )}
      </Button>

      {notificationsOpen && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[50] transition-opacity duration-300 ease-in-out"
          onClick={() => dispatch(toggleNotificationBar())}
        />
      )}

      <div
        ref={drawerRef}
        className={cn(
          "fixed top-0 right-0 h-full flex flex-col bg-card shadow-xl z-[60] transition-transform duration-300 ease-in-out",
          "w-full max-w-sm sm:max-w-md",
          notificationsOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-card-foreground">
            {t("dashboard.drawer.notifications")}
          </h2>
          <Button variant="ghost" size="icon" onClick={() => dispatch(toggleNotificationBar())} className="text-muted-foreground">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <NotificationCard
                key={notification._id}
                id={notification._id}
                title={notification.title}
                content={notification.content}
              />
            ))
          ) : (
            <div className="text-sm text-muted-foreground text-center py-10">
              {t("dashboard.notifications.message")}
            </div>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="p-4 border-t border-border">
            <Button 
              variant="outline"
              className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/50 hover:border-destructive"
              onClick={() =>
                dispatch(
                  deleteAllNotifications(
                    user.currentUser.user._id,
                    user.currentUser.token
                  )
                )
              }
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t("dashboard.notifications.clearAll")}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
